import React, { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FiCheck, FiStar } from 'react-icons/fi';
import { API_URL } from '../config/api';

const Feedback = () => {
  const { roomId } = useParams();
  const [searchParams] = useSearchParams();
  const meetingToken = searchParams.get('t');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [feedback, setFeedback] = useState({
    appointmentSatisfaction: null,
    schedulingEase: null,
    feltListened: null,
    carePlanClear: null,
    comments: ''
  });


  const handleSatisfactionClick = (value) => {
    setFeedback(prev => ({ ...prev, appointmentSatisfaction: value }));
  };

  const handleSchedulingClick = (value) => {
    setFeedback(prev => ({ ...prev, schedulingEase: value }));
  };

  const handleListenedClick = (value) => {
    setFeedback(prev => ({ ...prev, feltListened: value }));
  };

  const handleCarePlanClick = (value) => {
    setFeedback(prev => ({ ...prev, carePlanClear: value }));
  };

  const handleCommentsChange = (e) => {
    setFeedback(prev => ({ ...prev, comments: e.target.value }));
  };

  const handleSubmit = async () => {
    // At least one feedback question must be answered
    const hasAnyFeedback = feedback.appointmentSatisfaction ||
                          feedback.schedulingEase ||
                          feedback.feltListened !== null ||
                          feedback.carePlanClear !== null ||
                          feedback.comments.trim() !== '';

    if (!hasAnyFeedback) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/public/meeting/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          t: meetingToken,
          roomId,
          feedback: {
            appointmentSatisfaction: feedback.appointmentSatisfaction,
            schedulingEase: feedback.schedulingEase,
            feltListened: feedback.feltListened,
            carePlanClear: feedback.carePlanClear,
            comments: feedback.comments,
            timestamp: new Date().toISOString()
          }
        })
      });

      if (response.ok) {
        setSubmitted(true);
        // Redirect after 1.5 seconds
        setTimeout(() => {
          window.location.href = 'https://www.patient.brightlight.ai/login';
        }, 1500);
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    window.location.href = 'https://www.patient.brightlight.ai/login';
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-green-100 rounded-full mx-auto flex items-center justify-center">
            <FiCheck className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-light text-gray-900">Thank You!</h1>
          <p className="text-sm font-light text-gray-600">
            Your feedback helps us improve our service.
          </p>
          <p className="text-xs text-gray-500">
            Redirecting you to the main site...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <Card className="w-full max-w-4xl border-gray-200">
        <CardHeader className="space-y-1 py-4 sm:py-6">
          <CardTitle className="text-xl sm:text-2xl font-light text-center">
            How was your experience?
          </CardTitle>
          <p className="text-xs sm:text-sm font-light text-gray-600 text-center">
            Your feedback helps us improve our virtual consultation service
          </p>
        </CardHeader>
        <CardContent className="space-y-4 sm:space-y-5 lg:space-y-6 px-4 sm:px-6 lg:px-8">
          {/* Desktop: 2 columns grid, Mobile: Stack */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5 lg:gap-6">

            {/* Appointment Satisfaction */}
            <div className="space-y-2 sm:space-y-3">
              <p className="text-xs sm:text-sm font-medium text-gray-900">
                How was your appointment today?
              </p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 5, label: 'Very satisfied', stars: 5 },
                  { value: 4, label: 'Satisfied', stars: 4 },
                  { value: 3, label: 'Neutral', stars: 3 },
                  { value: 2, label: 'Dissatisfied', stars: 2 }
                ].map(option => (
                  <button
                    key={option.value}
                    onClick={() => handleSatisfactionClick(option.value)}
                    className={`py-1.5 sm:py-2 px-2 sm:px-3 rounded-lg border transition-all flex items-center gap-2 ${
                      feedback.appointmentSatisfaction === option.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex gap-0.5">
                      {[...Array(option.stars)].map((_, i) => (
                        <FiStar
                          key={i}
                          className={`w-2.5 h-2.5 sm:w-3 sm:h-3 ${
                            feedback.appointmentSatisfaction === option.value
                              ? 'fill-blue-600 text-blue-600'
                              : 'text-gray-400'
                          }`}
                        />
                      ))}
                    </div>
                    <span className={`text-xs sm:text-sm ${
                      feedback.appointmentSatisfaction === option.value
                        ? 'text-blue-700 font-medium'
                        : 'text-gray-700 font-light'
                    }`}>
                      {option.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Scheduling Ease */}
            <div className="space-y-2 sm:space-y-3">
              <p className="text-xs sm:text-sm font-medium text-gray-900">
                How easy was scheduling?
              </p>
              <div className="flex gap-2">
                {[
                  { value: 'easy', emoji: '😊', label: 'Easy' },
                  { value: 'neutral', emoji: '😐', label: 'Neutral' },
                  { value: 'difficult', emoji: '😟', label: 'Difficult' }
                ].map(option => (
                  <button
                    key={option.value}
                    onClick={() => handleSchedulingClick(option.value)}
                    className={`flex-1 py-2 sm:py-2.5 px-2 rounded-lg border transition-all text-center ${
                      feedback.schedulingEase === option.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className="text-lg sm:text-xl mb-0.5 block">{option.emoji}</span>
                    <span className={`text-xs ${
                      feedback.schedulingEase === option.value
                        ? 'text-blue-700 font-medium'
                        : 'text-gray-600 font-light'
                    }`}>
                      {option.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Felt Listened To */}
            <div className="space-y-2 sm:space-y-3">
              <p className="text-xs sm:text-sm font-medium text-gray-900">
                Did you feel listened to?
              </p>
              <div className="flex gap-2 sm:gap-3">
                <button
                  onClick={() => handleListenedClick(true)}
                  className={`flex-1 py-2 sm:py-2.5 px-3 sm:px-4 rounded-lg border transition-all flex items-center justify-center gap-2 ${
                    feedback.feltListened === true
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-base sm:text-lg">👍</span>
                  <span className={`text-xs sm:text-sm ${
                    feedback.feltListened === true
                      ? 'text-blue-700 font-medium'
                      : 'text-gray-700 font-light'
                  }`}>
                    Yes
                  </span>
                </button>
                <button
                  onClick={() => handleListenedClick(false)}
                  className={`flex-1 py-2 sm:py-2.5 px-3 sm:px-4 rounded-lg border transition-all flex items-center justify-center gap-2 ${
                    feedback.feltListened === false
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-base sm:text-lg">👎</span>
                  <span className={`text-xs sm:text-sm ${
                    feedback.feltListened === false
                      ? 'text-blue-700 font-medium'
                      : 'text-gray-700 font-light'
                  }`}>
                    No
                  </span>
                </button>
              </div>
            </div>

            {/* Care Plan Clear */}
            <div className="space-y-2 sm:space-y-3">
              <p className="text-xs sm:text-sm font-medium text-gray-900">
                Was your care plan clear?
              </p>
              <div className="flex gap-2 sm:gap-3">
                <button
                  onClick={() => handleCarePlanClick(true)}
                  className={`flex-1 py-2 sm:py-2.5 px-3 sm:px-4 rounded-lg border transition-all flex items-center justify-center gap-2 ${
                    feedback.carePlanClear === true
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-base sm:text-lg">✅</span>
                  <span className={`text-xs sm:text-sm ${
                    feedback.carePlanClear === true
                      ? 'text-blue-700 font-medium'
                      : 'text-gray-700 font-light'
                  }`}>
                    Yes
                  </span>
                </button>
                <button
                  onClick={() => handleCarePlanClick(false)}
                  className={`flex-1 py-2 sm:py-2.5 px-3 sm:px-4 rounded-lg border transition-all flex items-center justify-center gap-2 ${
                    feedback.carePlanClear === false
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-base sm:text-lg">❌</span>
                  <span className={`text-xs sm:text-sm ${
                    feedback.carePlanClear === false
                      ? 'text-blue-700 font-medium'
                      : 'text-gray-700 font-light'
                  }`}>
                    No
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* Comments Section - Full width */}
          <div className="space-y-2 pt-1 sm:pt-2">
            <p className="text-xs sm:text-sm font-medium text-gray-900">
              Any quick comments?
            </p>
            <textarea
              value={feedback.comments}
              onChange={handleCommentsChange}
              placeholder="Optional - Share any thoughts about your experience..."
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs sm:text-sm font-light text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows="2"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-1 sm:pt-2">
            <Button
              variant="outline"
              onClick={handleSkip}
              className="flex-1 font-light border-gray-200 text-xs sm:text-sm h-9 sm:h-10"
            >
              Skip
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                loading ||
                (!feedback.appointmentSatisfaction &&
                 !feedback.schedulingEase &&
                 feedback.feltListened === null &&
                 feedback.carePlanClear === null &&
                 feedback.comments.trim() === '')
              }
              className="flex-1 bg-blue-600 hover:bg-blue-700 font-light text-xs sm:text-sm h-9 sm:h-10"
            >
              {loading ? 'Submitting...' : 'Submit Feedback'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Feedback;